import { marked, Tokens } from 'marked'
import { markedSmartypants } from 'marked-smartypants'
import insane from 'insane'
import { deepMerge } from 'lib/object/objectUtils'

// Split markdown string into an array of strings
// Remove leading dashes from each line
export function convertMarkdownInclusionsToArray(markdown: string): string[] {
  return markdown?.split('\n').map(line => line.trim()
    .replace(/^[-]/, ''))
    .filter(line => line !== '')
}

// This library contains a custom link renderer intended for use with marked
// markdown rendering library. It adds the nofollow attribute to all anchors,
// with the exception of the following domains.
//
// Offer content sometimes contains links to external pages. We want these
// links to be only followed by Google when it is beneficial to LE.com.  We
// introduce a “safelist” for domains that are allowed to be followed.  The
// default behaviour should be to introduce a “nofollow” attribute to links
// that are not in this list.

const whitelist = [
  'facebook.com',
  'apple.com',
  'twitter.com',
  'play.google.com',
  'instagram.com',
  'youtube.com',
  'pinterest.com',
  'cudo.com.au',
  'deals.com.au',
  'treatme.co.nz',
  'luxuryescapes.com',
]

function whitelisted(url: string): boolean {
  return whitelist.some(domain => url.startsWith(`https://${domain}`) ||
      url.startsWith(`http://${domain}`) ||
      url.startsWith(`https://www.${domain}`) ||
      url.startsWith(`http://www.${domain}`))
}

export function markdownLinkRenderer(link: Tokens.Link): string {
  const { href, title, text } = link
  let rel = 'noopener noreferrer'
  if (!whitelisted(href)) {
    rel += ' nofollow'
  }
  let out = `<a target="_blank" rel="${rel}" href="${href}"`
  if (title) {
    out += ` title="${title}"`
  }
  out += `>${text}</a>`
  return out
}

marked.use({
  gfm: true,
  breaks: false,
  pedantic: false,
  useNewRenderer: true,
  renderer: {
    link: markdownLinkRenderer,
  },
})

marked.use(markedSmartypants())

const sanitiseOptions = deepMerge(insane.defaults, {
  allowedAttributes: {
    a: ['rel'],
  },
}, 'array-concat')

export function renderMarkdown(content: string): string {
  const html = marked.parse(content, { async: false }) as string
  const sanitisedHtml = insane(html, sanitiseOptions)
  return sanitisedHtml
}
